import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import profileService from "services/profileService";

const toMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

export default function useBusinessEntities() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState({ totalElements: 0, totalPages: 1 });

  const list = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await profileService.businessEntities.list(params);
      const content = Array.isArray(res?.content) ? res.content : [];
      setEntities(content);
      setPage({
        totalElements: res?.totalElements ?? content.length,
        totalPages: res?.totalPages ?? 1,
      });
      return res;
    } catch (err) {
      const message = toMessage(err, "Failed to load business entities");
      setError(message);
      toast.error(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const create = useCallback(async (payload, actorId) => {
    try {
      const res = await profileService.businessEntities.create(payload, actorId);
      toast.success("Business entity created");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to create business entity"));
      throw err;
    }
  }, []);

  const update = useCallback(async (id, payload, actorId) => {
    try {
      const res = await profileService.businessEntities.update(id, payload, actorId);
      toast.success("Business entity updated");
      return res;
    } catch (err) {
      toast.error(toMessage(err, "Failed to update business entity"));
      throw err;
    }
  }, []);

  const remove = useCallback(async (id, actorId) => {
    try {
      await profileService.businessEntities.delete(id, actorId);
      toast.success("Business entity removed");
    } catch (err) {
      toast.error(toMessage(err, "Failed to remove business entity"));
      throw err;
    }
  }, []);

  return { entities, loading, error, page, list, create, update, remove };
}

